import { Briefcase } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { formatDuration } from '../utils/timeCalculator';
import type { PositionTotal } from '../utils/timeCalculator';

interface PositionBreakdownProps {
  month: string;
  items: PositionTotal[];
  totalMinutes: number;
  selectedProjectId: string | null;
  onSelectProject: (projectId: string | null) => void;
  titlePrefix?: string;
  allLabel?: string;
}

export function PositionBreakdown({
  month,
  items,
  totalMinutes,
  selectedProjectId,
  onSelectProject,
  titlePrefix = 'Position',
  allLabel
}: PositionBreakdownProps) {
  if (items.length === 0) return null;

  const monthLabel = month ? format(parseISO(month + '-01'), 'MMMM yyyy') : '';
  const headerTitle = monthLabel
    ? `${titlePrefix} Hours Breakdown (${monthLabel})`
    : `${titlePrefix} Hours Breakdown`;
  const defaultAllLabel = allLabel || `All ${titlePrefix}s`;
  const itemNoun = titlePrefix.toLowerCase();

  return (
    <div className="bg-slate-50/80 rounded-xl p-5 border border-slate-200/60 shadow-xs">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
          <Briefcase className="w-4 h-4 text-indigo-500" />
          {headerTitle}
        </h4>
        <span className="text-[11px] text-slate-400 font-medium">
          Click a {itemNoun} to filter entries
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {/* All Positions / Projects button */}
        <button
          type="button"
          onClick={() => onSelectProject(null)}
          className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer ${
            selectedProjectId === null
              ? 'ring-2 ring-indigo-600 bg-indigo-50/80 border-indigo-300 shadow-xs'
              : 'bg-white hover:bg-slate-50/80 border-slate-200/70 shadow-xs hover:border-slate-300'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className={`text-xs font-bold truncate ${selectedProjectId === null ? 'text-indigo-900 font-extrabold' : 'text-slate-800'}`}>
                  {defaultAllLabel}
                </p>
                {selectedProjectId === null && (
                  <span className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md leading-none">
                    All
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                {(totalMinutes / 60 / 8.4).toFixed(1)}d total
              </p>
            </div>
            <span
              className={`text-xs font-bold px-2.5 py-1 rounded-lg border shrink-0 ${
                selectedProjectId === null
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-indigo-50 text-indigo-700 border-indigo-100/80'
              }`}
            >
              {formatDuration(totalMinutes)}
            </span>
          </div>
        </button>

        {/* Individual Position / Project buttons */}
        {items.map(p => {
          const isSelected = selectedProjectId === p.projectId;
          return (
            <button
              type="button"
              key={p.projectId}
              onClick={() => onSelectProject(isSelected ? null : p.projectId)}
              className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer ${
                isSelected
                  ? 'ring-2 ring-indigo-600 bg-indigo-50/80 border-indigo-300 shadow-xs'
                  : 'bg-white hover:bg-slate-50/80 border-slate-200/70 shadow-xs hover:border-slate-300'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p
                      className={`text-xs font-bold truncate ${isSelected ? 'text-indigo-900 font-extrabold' : 'text-slate-800'}`}
                      title={p.name}
                    >
                      {p.name}
                    </p>
                    {isSelected && (
                      <span className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md leading-none">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                    {(p.minutes / 60 / 8.4).toFixed(1)}d
                  </p>
                </div>
                <span
                  className={`text-xs font-bold px-2.5 py-1 rounded-lg border shrink-0 ${
                    isSelected
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-indigo-50 text-indigo-700 border-indigo-100/80'
                  }`}
                >
                  {formatDuration(p.minutes)}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
